import "./style.scss";

import Import from "assets/img/svg/navbar/import";
import Upload from "assets/img/svg/upload";
import ImportData from "components/ImportData";
import { getPendingImports } from "components/ImportData/utils";
import ModalBox from "components/Modal";
import { openEditMode } from "components/NewForm/utils";
import AuthPage from "components/shared/AuthPage";
import type { Action } from "components/shared/ButtonMenu";
import DetailPanelScroll from "components/shared/DetailPanelScroll";
import ElementList from "components/shared/ElementList";
import { EL_SORT_STORAGE_KEY } from "components/shared/ElementList/sortStorage";
import { LIST_SKELETON_COUNT } from "components/shared/TextSkeleton";
import Title from "components/Title";
import TitleFormContainer from "components/TitleFormContainer";
import BulkUpdatesContainer from "containers/BulkUpdatesContainer";
import NewTitleSimplifiedModal from "containers/Titles/NewTitleSimplifiedModal";
import { useAbility } from "hooks/useAbility";
import { useCompanySettings } from "hooks/useCompanySettings";
import useDetailPreviewLoading from "hooks/useDetailPreviewLoading";
import useEditModeLock from "hooks/useEditModeLock";
import usePageTitle from "hooks/usePageTitle";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/store";
import { exportTitleList, fetchTags } from "store/titleStore";
import type { TParamsPagination } from "store/types";
import { exportWorkList, fetchWorks, resetWorks } from "store/workStore";

import NewWorkModal from "./NewWorkModal";
import useWorksSelection from "./useWorksSelection";
import WorkDetail from "./WorkDetail";
import WorkListItem from "./WorkListItem";
import { DEFAULT_WORK_LIST_SORT, getWorksListParams } from "./worksListParams";

const Works = () => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const ability = useAbility();
    const { works, worksStatus, countWorks } = useAppSelector((state) => state.works);
    const tagTitleList = useAppSelector((state) => state.titles.tags);
    const { jobs } = useAppSelector((state) => state.jobs);
    const { pendingImports } = useAppSelector((state) => state.dataImport);
    const [isNewWorkOpen, setIsNewWorkOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [modalType, setModalType] = useState("");
    const { getCompanySettings } = useCompanySettings();
    const usesAgreementsOnly = getCompanySettings("uses_agreements_only");
    const canCreateWork = ability.can("create", "Work");
    const canCreateTitle = ability.can("create", "Title");
    const canUpdateWork = ability.can("update", "Work");
    const canDeleteWork = ability.can("delete", "Work");
    const isInitialLoad = worksStatus === "idle";
    const {
        selectedWorkId,
        selectedTitleId,
        expandedWorkId,
        selectedWork,
        selectedWorkStatus,
        isListReady,
        openWork,
        toggleWorkExpand,
        openTitle,
    } = useWorksSelection();
    const { isHeaderLoading } = useDetailPreviewLoading(
        selectedWorkId,
        selectedWork?.id,
        selectedWorkStatus,
        !isListReady,
    );

    const filterListToSelection =
        new URLSearchParams(location.search).get("filterList") === "1" ||
        Boolean((location.state as { filterList?: boolean } | null)?.filterList);
    const listWorks = useMemo(() => {
        if (filterListToSelection) return selectedWork ? [selectedWork] : [];
        return works;
    }, [filterListToSelection, selectedWork, works]);

    usePageTitle(t("Works.PageTitle"));
    useEditModeLock(editMode);

    useEffect(() => {
        if (worksStatus !== "idle") return;
        dispatch(fetchWorks(getWorksListParams()));
    }, [dispatch, worksStatus]);

    useEffect(() => {
        return () => {
            dispatch(resetWorks());
        };
    }, [dispatch]);

    useEffect(() => {
        if (tagTitleList === null) dispatch(fetchTags());
    }, [dispatch, tagTitleList]);

    useEffect(() => {
        if (selectedTitleId == null) setEditMode(false);
    }, [selectedTitleId]);

    const openNewTitle = (workId: number) => {
        if (!canCreateTitle) return;
        navigate(`/new-title?workId=${workId}`);
    };

    const openNewContract = (workId: number) => {
        const work = works.find((item) => item.id === workId) ?? (selectedWork?.id === workId ? selectedWork : null);
        navigate(`/work/${workId}/connect-beneficiar`, {
            state: { workId, work },
        });
    };

    const orderOptions = [
        { name: "name", label: t("Filters.WorkName") },
        { name: "createdAt", label: t("Filters.CreationDate") },
        { name: "updatedAt", label: t("Filters.LastModifiedDate") },
    ];

    const popoverMenu = {
        Imports: [
            ...(canCreateWork
                ? [
                      {
                          label: t("ImportData.ImportWork"),
                          icon: <Upload />,
                          action: () => setModalType("importWorks"),
                      },
                  ]
                : []),
            {
                label: t("ImportData.ImportTitle"),
                icon: <Upload />,
                action: () => setModalType("importTitles"),
            },
            {
                label: t("ImportData.ImportTerms"),
                icon: <Upload />,
                action: () => setModalType("importTerms"),
            },
            {
                label: t("BulkUpdates.BulkUpdate"),
                icon: <Upload />,
                action: () => setModalType("updates"),
            },
        ],
        Exports: [
            {
                label: t("ImportData.ExportWork"),
                icon: <Import />,
                action: () => dispatch(exportWorkList()),
            },
            {
                label: t("ImportData.ExportTitle"),
                icon: <Import />,
                action: () => dispatch(exportTitleList()),
            },
        ],
    };

    const pendingJobs = getPendingImports({ jobs, fakeImports: pendingImports, type: ["title"] });

    const createMenuActions: Action[] = [
        ...(canCreateWork
            ? [
                  {
                      name: t("Works.NewWork"),
                      action: () => setIsNewWorkOpen(true),
                  },
              ]
            : []),
        ...(canCreateTitle
            ? [
                  {
                      name: t("NewTitle.NewTitle"),
                      action: () => navigate("/new-title"),
                  },
                  ...(usesAgreementsOnly
                      ? [
                            {
                                name: t("NewTitle.QuickTitleCreation"),
                                action: () => setModalType("simpleCreation"),
                                subtitle: t("NewTitle.SimplifiedForm"),
                            },
                        ]
                      : []),
              ]
            : []),
    ];

    return (
        <AuthPage userAuthenticated pageName="works">
            <ElementList
                elements={listWorks}
                currentElement={selectedWorkId ?? -1}
                setCurrentElement={openWork}
                elementsStatus={worksStatus}
                elementCount={listWorks === works ? countWorks : listWorks.length}
                elementData={{
                    label: t("Works.PageTitle"),
                    actionLabel: t("Works.NewWork"),
                    action: () => createMenuActions[0]?.action?.(),
                    actionDisabled: createMenuActions.length === 0,
                }}
                hasMultipleActionBtn={createMenuActions.length > 1}
                btnMenuActions={createMenuActions}
                elementSearchFunc={(options: TParamsPagination) => dispatch(fetchWorks(options))}
                tagList={tagTitleList}
                showTags
                orderOptions={orderOptions}
                isInitialLoad={isInitialLoad}
                showListLoading={!isListReady}
                listSkeleton={Array.from({ length: LIST_SKELETON_COUNT }, (_, index) => (
                    <WorkListItem
                        key={`work-list-skeleton-${index}`}
                        currentWorkId={null}
                        currentTitleId={null}
                        isExpanded={false}
                        titlesCountLabel=""
                        onOpen={() => undefined}
                        onToggleExpand={() => undefined}
                        onOpenTitle={() => undefined}
                        isLoading
                        skeletonIndex={index}
                    />
                ))}
                defaultFilterOptions={DEFAULT_WORK_LIST_SORT}
                sortStorageKey={EL_SORT_STORAGE_KEY.works}
                popoverMenu={popoverMenu}
                pendingJobs={pendingJobs}
                editMode={editMode}
            >
                <>
                    {listWorks.map((work) => (
                        <WorkListItem
                            key={work.id}
                            work={work}
                            currentWorkId={selectedWorkId}
                            currentTitleId={selectedTitleId}
                            isExpanded={expandedWorkId != null && Number(expandedWorkId) === Number(work.id)}
                            titlesCountLabel={
                                work.titles == null ? null : t("Works.TitlesCount", { count: work.titles.length })
                            }
                            onOpen={openWork}
                            onToggleExpand={toggleWorkExpand}
                            onOpenTitle={openTitle}
                            editMode={editMode}
                        />
                    ))}
                </>
            </ElementList>

            <DetailPanelScroll className="works-right-panel">
                {isHeaderLoading ? (
                    <WorkDetail
                        isLoading
                        onAddTitle={openNewTitle}
                        onAddContract={openNewContract}
                        onOpenTitle={() => undefined}
                        canAddTitle={canCreateTitle}
                    />
                ) : selectedTitleId != null && selectedWork ? (
                    <>
                        <div className="works-right-panel__fade">
                            <Title
                                titleId={selectedTitleId}
                                editTitle={editMode}
                                openEditTitle={(formStep) =>
                                    openEditMode({
                                        elementId: String(selectedTitleId),
                                        setEditMode,
                                        formStep,
                                    })
                                }
                            />
                        </div>
                        {editMode && (
                            <TitleFormContainer
                                mode="edit-page"
                                editModeActive={editMode}
                                closeEditMode={() => setEditMode(false)}
                            />
                        )}
                    </>
                ) : selectedWork ? (
                    <WorkDetail
                        work={selectedWork}
                        onAddTitle={openNewTitle}
                        onAddContract={openNewContract}
                        onOpenTitle={(titleId) => openTitle(selectedWork.id, titleId)}
                        onDeleted={() => navigate("/works")}
                        canAddTitle={canCreateTitle}
                        canUpdate={canUpdateWork}
                        canDelete={canDeleteWork}
                    />
                ) : (
                    <section className="works-placeholder">
                        <p className="works-placeholder__title">{t("Works.SelectWork")}</p>
                        <p className="works-placeholder__hint">{t("Works.SelectWorkHint")}</p>
                    </section>
                )}
            </DetailPanelScroll>

            {canCreateWork && (
                <NewWorkModal
                    open={isNewWorkOpen}
                    onClose={() => setIsNewWorkOpen(false)}
                    onCreated={(work) => {
                        setIsNewWorkOpen(false);
                        navigate(`/works/${work.id}`);
                    }}
                />
            )}
            {canCreateTitle && (
                <NewTitleSimplifiedModal open={modalType === "simpleCreation"} onClose={() => setModalType("")} />
            )}

            <ModalBox
                open={modalType === "importWorks"}
                dictionaryKey={"works"}
                onClose={() => setModalType("")}
                mainTitle={t("ImportData.ImportWork")}
            >
                <ImportData dictionaryKey={"works"} close={() => setModalType("")} />
            </ModalBox>
            <ModalBox
                open={modalType === "importTitles"}
                dictionaryKey={"titles"}
                onClose={() => setModalType("")}
                mainTitle={t("ImportData.ImportTitle")}
            >
                <ImportData dictionaryKey={"titles"} close={() => setModalType("")} />
            </ModalBox>
            <ModalBox
                open={modalType === "importTerms"}
                dictionaryKey={"terms"}
                onClose={() => setModalType("")}
                mainTitle={t("ImportData.ImportTerms")}
            >
                <ImportData dictionaryKey={"terms"} close={() => setModalType("")} />
            </ModalBox>
            {modalType === "updates" && (
                <BulkUpdatesContainer
                    openModal={modalType === "updates"}
                    closeModal={() => setModalType("")}
                    from="titles"
                />
            )}
        </AuthPage>
    );
};

export default Works;
